// tslint:disable: no-eval max-line-length
import { CanActivate, ActivatedRouteSnapshot, UrlTree, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { CallApiMethodsService } from '../services/AsynchronousRequestServices/call-api-methods.service';
import { UserInformationService } from '../services/TemplateServices/user-information.service';
import { SharedFunctionsService } from '../services/TemplateServices/shared-functions.service';
import { NotifyService } from '../services/TemplateServices/notify.service';

import { User, Menu, Platform, Modules, Alerts } from '../interfaces/user';
import { ErrorStructure } from '../interfaces/management';
import { StringValues } from '../interfaces/undetermined';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {

  constructor(
    private callApiMethods: CallApiMethodsService,
    private sharedMethods: SharedFunctionsService,
    private user: UserInformationService,
    private notify: NotifyService,
    private router: Router,
    private title: Title,
  ) { }

  /* canActivate(): Método que permite/deniega el acceso a un componente según los permisos de usuario
    next: Contiene la información del componente al que se accederá
  */
  canActivate(next: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.checkLogin(next).then((flag: boolean) => {
      // ↓ Actualiza el título de la página de acuerdo al módulo accedido
      if (["35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49", "50", "52", "54", "71"].includes(`${next.data.token}`)) {
        this.title.setTitle("Indicadores Junta Directiva");
      } else if (["18", "19", "26", "27", "61", "70", "74", "79"].includes(`${next.data.token}`)) {
        this.title.setTitle("Indicadores Contact Center");
      } else if (["72", "73", "75", "76", "78"].includes(`${next.data.token}`)) {
        this.title.setTitle(`GC Balanced - Reportes`);
      } else if (["1", "2", "3", "4"].includes(`${next.data.token}`)) {
        this.title.setTitle(`GC Balanced - ${this.user.entityName}`);
      } else if (["53", "62", "63"].includes(`${next.data.token}`)) {
        this.title.setTitle("Indicadores Red Financiera");
      } else if (["64", "66"].includes(`${next.data.token}`)) {
        this.title.setTitle("GC Balanced - Mapa Estratégico");
      } else if (["69"].includes(`${next.data.token}`)) {
        this.title.setTitle("Indicadores Red Medios y Marketing");
      } else if (["60"].includes(`${next.data.token}`)) {
        this.title.setTitle("Indicadores Red Operaciones");
      } else if (["56"].includes(`${next.data.token}`)) {
        this.title.setTitle("Indicadores Red Tecnología");
      } else if (["55"].includes(`${next.data.token}`)) {
        this.title.setTitle("Indicadores Red Jurídica");
      } else {
        this.title.setTitle("GC Balanced");
      }
      // ↓ Asigna la página actual al servicio de usuario
      this.user.currentPage = `${next.data.token}`;
      // Replica cambios a los suscriptores cambios
      this.user.emitter.next(true);
      // ↓ Si flag es verdadero, se permitirá el acceso a la ruta, en caso contrario se redireccionará a una específica
      return flag;
    });
  }

  /* checkLogin(): Método que verifica, inicia y actualiza la sesión del usuario y los permisos del componente
    route: Contiene la información del componente al que se accederá
  */
  checkLogin(route: ActivatedRouteSnapshot): Promise<boolean> {
    // ↓ Devuelve una promesa que si es verdadera permite acceso al módulo, si es falsa lo redireccionará
    return new Promise((resolve: (value?: boolean | PromiseLike<boolean>) => void, reject: (reason?: any) => void) => {
      // pageToken: Contiene el id del componente al que se accederá
      const pageToken: string = route.data.token;
      // starting: Verifica si el usuario está conectado, activo o inciando sesión
      const starting: boolean = this.checkUserToken();
      // ↕ Si es verdadero el usuario esta iniciando sesión
      if (starting) {
        // ↓ Obtiene las credenciales del usuario
        this.callApiMethods.sendAsynchronousRequest("users", "logIn").then((data: User) => {
          // Guarda las credenciales
          this.setUser(data);
          // Genera el menú del usuario y permite el acceso al inicio
          resolve(this.setUserMenu().then((flag: boolean) => flag));
        }).catch(() => this.sharedMethods.redirectToLogInPage());
      } else {
        // ↑ Si starting es falso, entonces el usuario tiene sesión activa
        // ↓ Verifica si las credenciales del usuario están definidas
        if (this.user.userPermits) {
          // ↓ Verifica si el usuario tiene acceso al componente
          if (this.user.userPermits.includes(pageToken.toString())) {
            // ↓ Actualiza el tiempo de sesión del usuario
            this.sharedMethods.updateTime();
            // ↓ Permite acceso al componente
            resolve(true);
          } else {
            // ↓ Redirecciona al inicio
            this.router.navigate([this.user.userHome]);
            // ↓ Cancela el acceso al componente
            resolve(false);
          }
        } else {
          // ↓ Obtiene las credenciales del usuario
          this.callApiMethods.sendAsynchronousRequest("users", "logIn").then((data: User) => {
            // navStorage: Obtiene el menu del usuario guardado en storage
            const navStorage: string = localStorage.getItem("nav");
            // modStorage: Obtiene los módulos del usuario guardados en storage
            const modStorage: string = localStorage.getItem("mods");
            // ↓ Verifica si el menú está definido para el usuario
            if ((navStorage && JSON.parse(navStorage)[this.user.userToken]) && (modStorage && JSON.parse(modStorage)[this.user.userToken])) {
              // ↓ Verifica si el usuario aún está activo
              if (this.sharedMethods.checkTime(JSON.parse(navStorage).time ? JSON.parse(navStorage).time : 0) && this.sharedMethods.checkTime(JSON.parse(modStorage).time ? JSON.parse(modStorage).time : 0)) {
                // ↓ Guarda el menú del usuario
                this.user.userMenu = { ...this.user.userMenu, ...this.dMenu(JSON.parse(navStorage)[this.user.userToken]) as Menu };
                // ↓ Guarda los módulos del usuario
                this.user.modules = [...(this.user.modules || []), ...this.dMenu(JSON.parse(modStorage)[this.user.userToken]) as Modules[]];
                // Guarda las credenciales del usuario
                this.setUser(data);
                // ↓ Verifica si el usuario tiene acceso al componente y le pemite el acceso
                if (this.user.userPermits.includes(pageToken.toString())) { resolve(true); } else {
                  // ↓ Redirecciona al inicio
                  this.router.navigate([this.user.userHome]);
                  // ↓ Cancela el acceso al componente
                  resolve(false);
                }
              } else { this.sharedMethods.redirectToLogInPage(); }
              // ↑ Si el usuario está inactivo redirecciona a iniciar sesión
            } else { resolve(this.setUserMenu().then((flag: boolean) => flag)); }
            // ↑ Genera el menú del usuario y permite el acceso al inicio
          }).catch(() => this.sharedMethods.redirectToLogInPage());
        }
      }
    });
  }

  /* setUser(): Método que guarda las credenciales del usuario
    data: Credenciales
  */
  async setUser(data: User) {
    // ↓^7 Asigna las credenciales al servicio del usuario
    this.user.userPermits = data.sess_mods;
    this.user.userNetwork = data.sess_red;
    this.user.userLinks = data.sess_links;
    this.user.userName = data.sess_nomUsr;
    this.user.userHome = data.sess_page;
    this.user.userType = data.sess_tipo;
    this.user.userId = data.sess_user;
    // ↓ Verifica las plataformas a las que tiene acceso el usuairo
    if (this.user.userLinks) {
      // ↓ Itera las plataformas para asignar link ignorando GC Balanced para no redireccionar a la misma app
      this.user.userLinks.plataformas = this.user.userLinks.plataformas
        .filter((elm: Platform) => [1, 2, 4, 5, 6].includes(Number(elm.ptf_id))) // Plataforma 3 es GC Balanced
        .map((elm: Platform) => {
          switch (Number(elm.ptf_id)) {
            // (↓^5) Asigna el link a cada plataforma
            case 1: elm.ptf_link = `https://www.gcm.garantiascomunitarias.com/client/index.php?u=${this.user.userToken}&p=1`; break;
            case 2: elm.ptf_link = `https://garantiascomunitarias.com/`; break;
            case 4: elm.ptf_link = `https://www.repotencia.com/login/start_session.php?u=${this.user.userToken}&p=4`; break;
            case 5: elm.ptf_link = `https://www.burodeconexiones.com/buro_nuevo/interno/redireccion.php?u=${this.user.userToken}&p=5`; break;
            case 6: elm.ptf_link = `https://www.gccommunity.co/session/start_session.php?u=${this.user.userToken}&p=6`; break;
          }
          // ↓ Devuelve el id de la plataforma índice actual más el link
          return elm;
        });
    }
    // ↓ Verifica el tipo de usuario (2)->Entidad, (4)->Empleado GC
    if (data.sess_tipo === "2") {
      // ↓↓↓ Asigna las credenciales de la entidad al servicio del usuario
      this.user.entityAgreement = data.sess_conv;
      this.user.entityName = data.sess_nomEnt;
      this.user.entityId = data.sess_entd;
    } else {
      // ↓↓ dashboardStorage, dashboardStorage. Obtiene información de la entidad guardada en el storage
      const dashboardStorage: string = localStorage.getItem("dashboard");
      const entityStorage: string = localStorage.getItem("entity");
      // ↓ Verifica si hay información de entidad definida
      if (dashboardStorage && JSON.parse(dashboardStorage)[this.user.userToken]) {
        // ↓ Verifica si el usuario aún está activo
        if (this.sharedMethods.checkTime(JSON.parse(dashboardStorage).time ? JSON.parse(dashboardStorage).time : 0)) {
          // ↓ Actualiza el menu del usuario con el menú de la entidad
          this.user.userMenu = { ...this.user.userMenu, ...this.dMenu(JSON.parse(dashboardStorage)[this.user.userToken]) as Menu };
          // ↓↓↓ Asigna las credenciales de la entidad al servicio del usuario
          this.user.entityAgreement = JSON.parse(entityStorage)[this.user.userToken][2];
          this.user.entityName = JSON.parse(entityStorage)[this.user.userToken][1];
          this.user.entityId = JSON.parse(entityStorage)[this.user.userToken][0];
        } else { this.sharedMethods.redirectToLogInPage(); }
        // ↑ Si el usuario está inactivo redirecciona a iniciar sesión
      }

      // alertStorage: Obtiene información de las alertas guardada en el storage
      const alertStorage: string = localStorage.getItem("alerts");
      // ↓ Para la ejecución mientars ingresa información de las alertas en el storage
      await (() => new Promise<void>((res: () => void) => {
        // ↓ Verifica si hay información de alertas definida
        if (alertStorage && JSON.parse(alertStorage)[this.user.userToken]) {
          // ↓ Verifica si el usuario aún está activo
          if (this.sharedMethods.checkTime(JSON.parse(alertStorage).time ? JSON.parse(alertStorage).time : 0)) {
            // ↓ Actualiza la información de las alertas
            this.user.userAlerts = JSON.parse(alertStorage)[this.user.userToken];
            // ↓ Finaliza la promesa
            res();
          }
        } else {
          // ↓ Actualiza la información de las alertas
          this.callApiMethods.sendAsynchronousRequest("users", "getAlertPermissions").then((alerts: Alerts | ErrorStructure) => {
            if (!((alerts as ErrorStructure).requestStatus === false)) {
              this.user.userAlerts = alerts as Alerts;
              // ↓ Guarda la información de las alertas en el storage
              localStorage.setItem("alerts", JSON.stringify({ [this.user.userToken]: alerts, time: new Date().getTime() }));
            } else { M.toast({ html: "Imposible mostrar notificaciones", classes: "background-danger white-text" }); }
            // ↓ Finaliza la promesa
            res();
          });
        }
      }))();
      // ↓ Añade el intervalo para notificar reclamaciones si el usuario tiene permisos
      if (this.user.userAlerts.claimsFulfillment) { this.notify.notify("claimsFulfillment", 1200000, "Hay reclamaciones pendientes por gestionar", "networkIndicators", "getClaimIndicators", { operation: 2 }); }
    }
  }

  // setUserMenu(): Método que obtiene el menú del usuario desde el API
  async setUserMenu(): Promise<boolean> {
    // ↓ Devuelve una promesa que si es verdadera permite acceso al módulo, si es falsa lo redireccionará
    return new Promise((resolve: (value?: boolean | PromiseLike<boolean>) => void, reject: (reason?: any) => void) => {
      // ↓ Llama el servicio del menú
      const menuPromise: Promise<StringValues> = this.callApiMethods.sendAsynchronousRequest("users", "getMenu");
      // ↓ Llama el servicio de los módulos
      const modulePromise: Promise<StringValues> = this.callApiMethods.sendAsynchronousRequest("users", "getModules");
      Promise.all([menuPromise, modulePromise]).then(([menu, modules]: StringValues[]) => {
        // ↓ Guarda el menú en el storage
        localStorage.setItem("nav", JSON.stringify({ ...menu, time: new Date().getTime() }));
        // ↓ Guarda los módulos en el storage
        localStorage.setItem("mods", JSON.stringify({ ...modules, time: new Date().getTime() }));
        // ↓ Guarda el menú en el servicio de usuario
        this.user.userMenu = this.dMenu(menu[this.user.userToken]) as Menu;
        // ↓ Guarda los módulos en el servicio de usuario
        this.user.modules = this.dMenu(modules[this.user.userToken]) as Modules[];
        // ↓ Redirige al incio
        this.router.navigate([this.user.userHome]);
        // ↓ Cancela la navegación al componente
        resolve(false);
      }).catch(() => this.sharedMethods.redirectToLogInPage());
    });
  }

  // checkUserToken(): Verifica si el usuario está conectado, activo o inciando sesión
  checkUserToken(): boolean {
    // ↓ Verifica si el token de usuario no está definido en el servicio de usuario
    if (!this.user.userToken) {
      // ↓ Verifica si se está iniciando sesión
      if (new URLSearchParams(window.location.search).has('u')) {
        // ↓ Elimina toda la información de usuario almacenada en storage
        this.sharedMethods.logOff();
        // token, platform. Obtiene la información del usuario recibida desde la URL
        const token = new URLSearchParams(window.location.search).get('u');
        const platform = new URLSearchParams(window.location.search).get('p');
        // ↓ Guarda la información de la URL en el servicio de usuario
        [this.user.userToken, this.user.platform] = [token, platform];
        // ↓ Guarda la información de la URL en el storage
        localStorage.setItem("user", JSON.stringify({ token, platform, time: new Date().getTime() }));
        // Devuelve verdader (Usuario inicia sesión)
        return true;
      } else { return this.checkUser(); }
      // ↑ Si no está inciando sesión verifica si el token está definido y activo en el storage
    } else { return this.checkUser(); }
    // ↑ Si el token de usuario está definido verifica si está activo
  }

  // checkUser(): Método que verifica si el usuario esá definido y activo en el storage
  checkUser(): boolean {
    // userStorage: Obtiene información de usuario guardada en el storage
    const userStorage: string = localStorage.getItem("user");
    // ↓ Verifica si hay información de usuario definida
    if (userStorage) {
      // ↓ Verifica si el usuario aún está activo
      if (this.sharedMethods.checkTime(JSON.parse(userStorage).time ? JSON.parse(userStorage).time : 0)) {
        // ↓↓ Actualiza el servicio de usuario con la información del storage
        this.user.userToken = JSON.parse(userStorage).token;
        this.user.platform = JSON.parse(userStorage).platform;
        // ↓ Devuelve false (Usuario con sesión iniciada)
        return false;
      }
    }
    // ↓ Si el usuario no está definido o activo devuelve a iniciar sesión
    this.sharedMethods.redirectToLogInPage();
  }

  // dMenu(): Formatea el menú
  dMenu(value: string): Menu | Modules[] {
    const dec: any = eval("UTF8ArrToStr")(eval("base64DecToArr")(value));
    const arr: {} = dec.split("gkhcdkh42fdsdf67kjhkjVDFHDS65ksd");
    const txt: any = eval("UTF8ArrToStr")(eval("base64DecToArr")(arr[1]));
    return JSON.parse(txt);
  }

}
